import { FC } from "react";
import { Modal, Button, Loader } from "@mantine/core";
import { IconCircleCheck, IconExclamationCircle } from "@tabler/icons-react";
import { WallexUploadError } from "../InfoDocWallex";

interface WallexConfirmModalProps {
  isOpen: boolean;
  isUploading: boolean;
  isCompleted: boolean;
  uploadError: WallexUploadError | null;
  entityName: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  onClearError: () => void;
}

const WallexConfirmModal: FC<WallexConfirmModalProps> = ({
  isOpen,
  isUploading,
  isCompleted,
  uploadError,
  entityName,
  onClose,
  onConfirm,
  onClearError,
}) => {
  const handleClose = () => {
    if (!isUploading) {
      onClose();
      onClearError();
    }
  };

  const title = uploadError
    ? "Upload Failed to Wallex"
    : isCompleted
      ? "Submission Successful"
      : isUploading
        ? "Submitting to Wallex..."
        : "Confirm Submission to Wallex";

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title={<b>{title}</b>}
      centered
      closeOnClickOutside={!isUploading}
      closeOnEscape={!isUploading}
    >
      {uploadError ? (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <IconExclamationCircle
            color="red"
            size={32}
            style={{ marginBottom: "15px" }}
          />
          <p
            style={{ color: "red", fontWeight: "bold", marginBottom: "10px" }}
          >
            Upload Failed
          </p>
          <p style={{ fontSize: "14px", color: "#666" }}>
            Please contact the Tech team for assistance and provide a
            screenshot of this error message
          </p>
          <p style={{ marginBottom: "10px" }}>{uploadError.message}</p>
          <p style={{ marginBottom: "10px" }}>
            {uploadError.response?.data?.message ? (
              <>{uploadError.response?.data?.message}</>
            ) : (
              uploadError?.message ||
              "An unknown error occurred during the upload process"
            )}
          </p>
          {uploadError?.response?.messages ||
            (uploadError && (
              <div
                style={{
                  textAlign: "left",
                  marginTop: "10px",
                  padding: "10px",
                  backgroundColor: "#fff0f0",
                  border: "1px solid #ffcccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              >
                <strong>Error details:</strong>
                <ul
                  style={{
                    marginTop: "5px",
                    paddingLeft: "20px",
                    padding: "10px",
                  }}
                >
                  {typeof uploadError?.response?.data?.errors === "object" &&
                  uploadError?.response?.data?.failedDocuments ? (
                    Object.entries(uploadError.response!.data!.errors!).map(
                      ([key, value]) => (
                        <li key={key}>
                          <strong>{key}:</strong>{" "}
                          {Array.isArray(value)
                            ? value.join(", ")
                            : String(value)}
                        </li>
                      )
                    )
                  ) : Array.isArray(uploadError.response?.data?.message) ? (
                    uploadError.response?.data?.message.map(
                      (msg: string, i: number) => <li key={i}>{msg}</li>
                    )
                  ) : (
                    <li>{uploadError.response?.data?.message}</li>
                  )}
                </ul>
              </div>
            ))}
          {uploadError && uploadError.response?.data?.failedDocuments && (
            <div
              style={{
                textAlign: "left",
                border: "1px solid #ffcccc",
                borderRadius: "4px",
                padding: "10px",
                backgroundColor: "#fff0f0",
                marginBottom: "15px",
              }}
            >
              <p>
                <strong>
                  Failed documents (
                  {uploadError.response!.data!.failedDocuments!.length}):
                </strong>
              </p>
              <div
                style={{
                  maxHeight: "200px",
                  overflowY: "auto",
                  paddingRight: "5px",
                  marginBottom: "5px",
                }}
              >
                {Array.isArray(uploadError.response!.data!.failedDocuments) ? (
                  uploadError.response!.data!.failedDocuments.map(
                    (
                      doc: {
                        fileName?: string;
                        errorMessage?: string;
                        error?: string;
                        supportMessage?: string;
                      },
                      i: number
                    ) => (
                      <div
                        key={i}
                        style={{
                          margin: "8px 0",
                          padding: "5px 0",
                          borderBottom:
                            i <
                            uploadError.response!.data!.failedDocuments!
                              .length -
                              1
                              ? "1px solid #eee"
                              : "none",
                        }}
                      >
                        <div>
                          <strong>File:</strong>{" "}
                          {typeof doc.fileName === "string"
                            ? doc.fileName
                            : "[Unknown]"}
                        </div>
                        <div>
                          <strong>Error:</strong>
                          {""}
                          {doc.errorMessage ||
                            doc.error ||
                            "Failed to upload document(s)"}
                        </div>
                      </div>
                    )
                  )
                ) : (
                  <p>No detailed error information available</p>
                )}
              </div>
            </div>
          )}
          {/* D5 fix: single close button instead of duplicated if/else */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "10px",
              marginTop: "15px",
            }}
          >
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      ) : isUploading ? (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div className="spinner" style={{ marginBottom: "15px" }}>
            <Loader size="md" />
          </div>
          <p>Submitting onboarding information and documents to Wallex...</p>
        </div>
      ) : isCompleted ? (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <IconCircleCheck
            color="green"
            size={40}
            style={{ marginBottom: "15px" }}
          />
          <p
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              color: "green",
              marginBottom: "10px",
            }}
          >
            Submission Successful
          </p>
          <p>
            Onboarding information and documents have been successfully sent to
            Wallex.
          </p>
        </div>
      ) : (
        <>
          <p>
            The onboarding information and documents for {entityName} will be
            sent to Wallex.
          </p>

          <div
            style={{
              marginTop: 20,
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <Button variant="outline" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button color="blue" onClick={onConfirm}>
              Confirm
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default WallexConfirmModal;
